import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { Button, FormGroupLabel, DatePicker, CheckboxGroup } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { Form, FormSpacer, RadioGroup, InformationPopUp, CurrencyTextField } from 'components';
import { DropdownController, TextFieldController, ToggleController } from 'controllers';
import dayjs from 'dayjs';
import { FormStepContentProps } from 'types';
import { useValidate } from 'hooks/useValidate';
import { useScrollTop } from 'hooks/useScrollTop';
import { ComplaintDetailsFormFields, FormDataType, useFormState } from 'contexts/FormProvider';
import Attachment from 'components/Attachment/Attachment';
import validationSchema from './schema';

import { useContentContext } from '@qbe/dfc-content';
import {
  ClaimCategoryList,
  FinancialHardshipCategoryList,
  OutcomeTypeList,
  ProductTypeList,
  SalesCategoryList,
  SourceOfComplaintList,
} from './enum';
import { YesNoList } from 'utils/constants';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { useTrackApiError } from 'hooks/useAnalytics';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from '@emotion/styled';
import get from 'lodash/get';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';

export const CheckboxGroupStyled = styled(CheckboxGroup)`
  align-items: center;
  position: static;
`;

const ComplaintDetailsForm = ({ defaultValues, onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  const { maxFileSize = 0, enableAttachment } = useCheckWindowCMS();
  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const TEXT_CONTENT = {
    formHeading: getTextContent('complaint.form.heading'),
    heading: getTextContent('complaint.details.heading'),
    complaintDate: getTextContent('complaint.details.complaintDate'),
    sourceOfComplaint: getTextContent('complaint.details.sourceOfComplaint'),
    acknowledgement: getTextContent('complaint.details.acknowledgement'),
    acknowledgementInfoText: getTextContent('complaint.details.acknowledgement.infoText'),
    acknowledgementDate: getTextContent('complaint.details.acknowledgementDate'),
    product: getTextContent('complaint.details.product'),
    complaintDescription: getTextContent('complaint.details.complaintDescription'),
    complaintType: getTextContent('complaint.details.complaintType'),
    complaintTypeInfoText: getTextContent('complaint.details.complaintType.infoText'),
    compTypeSalesText: getTextContent('complaint.details.compType.sales.text'),
    compTypeClaimsText: getTextContent('complaint.details.compType.claims.text'),
    compTypeFinHardText: getTextContent('complaint.details.compType.finHard.text'),
    complaintCategory: getTextContent('complaint.details.complaintCategory'),
    complaintCategoryInfoText: getTextContent('complaint.details.complaintCategory.infoText'),
    isSensitive: getTextContent('complaint.details.isSensitive'),
    sensitiveInfoText: getTextContent('complaint.details.sensitive.infoText'),
    isResolved: getTextContent('complaint.details.isResolved'),
    isResolvedInfoText: getTextContent('complaint.details.isResolved.infoText'),
    customerDesiredOutcome: getTextContent('complaint.details.customer.desiredOutcome'),
    resolvedDate: getTextContent('complaint.details.resolvedDate'),
    complaintOutcome: getTextContent('complaint.details.complaint.outcome'),
    outcomeDescription: getTextContent('complaint.details.outcomeDescription'),
    amountInDispute: getTextContent('complaint.details.amountInDispute'),
    amountInDisputeLabel: getTextContent('complaint.details.amountInDispute.label'),
    amountResolved: getTextContent('complaint.details.amountResolved'),
    amountResolvedLabel: getTextContent('complaint.details.amountResolved.label'),
  };
  const { formData } = useFormState();
  const schema = validationSchema({ maxFileSize });
  const form = useForm<ComplaintDetailsFormFields>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(schema) as any,
  });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    register,
    trigger,
  } = form;

  React.useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const { isFieldRequired } = useIsFieldRequired(form, schema);
  const isValid = schema.isValidSync(watch());
  const values = watch();
  const resolved = watch('resolved');
  const compType = watch('compType') || null;
  const acknowledgement = watch('acknowledgement');

  const isCompTypeEnabled = !isEmpty(compType) ? true : false;

  const userData = () => {
    if (resolved === 'yes') {
      reset({
        desiredOutcome: undefined,
      });
      onComplete({
        desiredOutcome: undefined,
      });
    } else {
      reset({
        outcomeDesc: undefined,
        outcomeType: undefined,
        resolvedDate: undefined,
      });
      onComplete({
        outcomeDesc: undefined,
        outcomeType: undefined,
        resolvedDate: undefined,
      });
    }
  };

  const compCategoryOptions = {
    sales: SalesCategoryList,
    claims: ClaimCategoryList,
    financialHardshipApp: FinancialHardshipCategoryList,
    default: [],
  }[compType ?? 'default'];

  const { error: apiError } = useValidate(userData, onComplete);

  useTrackApiError(apiError);

  // Attachment
  React.useEffect(() => {
    register('files');
  }, [register]);

  const { files = [] } = values;

  const setFileValue = (newFiles: File[]) => {
    setValue('files', newFiles);
    trigger('files');
  };

  const updateFiles = (newFiles: FileList) => setFileValue([...files, ...Object.values(newFiles)]);
  const setFiles = (newFiles: File[]) => setFileValue(newFiles);

  const onSubmit = (data: Partial<FormDataType>) => {
    let newData = data;

    if (newData.resolved === 'yes') {
      newData = {
        ...newData,
        desiredOutcome: '',
      };
    }

    if (newData.resolved === 'no') {
      newData = {
        ...newData,
        resolvedDate: null,
        outcomeType: '',
        outcomeDesc: '',
      };
    }

    if (newData.acknowledgement === 'no') {
      newData = {
        ...newData,
        acknowledgementDate: null,
      };
    }

    onComplete({ ...formData, ...newData });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} data-test="complaint-details-form">
      <FormCard title={TEXT_CONTENT.formHeading} description="" variant="pageTitle" />
      <FormCard title={TEXT_CONTENT.heading} variant="sectionTitle">
        <FormGroupLabel title={TEXT_CONTENT.complaintDate} required alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="complaintDate"
          control={control}
          render={({ field: { value, ...field }, fieldState: { error } }) => {
            return (
              <DatePicker
                {...field}
                value={dayjs(value as any)}
                id={field.name}
                format="DD/MM/YYYY"
                label="DD/MM/YYYY"
                data-track={field.name}
                data-track-value="mask"
                maxDate={dayjs()}
                data-test={field.name}
                error={!!error}
                helperText={error?.message}
              />
            );
          }}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('sourceOfComplaint')}
          title={TEXT_CONTENT.sourceOfComplaint}
        />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          capture
          label="Source of complaint"
          name="sourceOfComplaint"
          optionList={SourceOfComplaintList.map((value) => ({
            label: value,
            value,
          }))}
        />

        <ToggleController
          required
          control={control}
          name="acknowledgement"
          options={YesNoList}
          capture
          title={TEXT_CONTENT.acknowledgement}
          titleSuffix={<InformationPopUp message={TEXT_CONTENT.acknowledgementInfoText} />}
        />

        {acknowledgement === 'yes' && (
          <>
            <FormGroupLabel title={TEXT_CONTENT.acknowledgementDate} required alignCenter />
            <FormSpacer type="input" />
            <Controller
              name="acknowledgementDate"
              control={control}
              render={({ field: { value, ...field }, fieldState: { error } }) => {
                return (
                  <DatePicker
                    {...field}
                    value={dayjs(value as any)}
                    id={field.name}
                    format="DD/MM/YYYY"
                    label="DD/MM/YYYY"
                    data-track={field.name}
                    data-track-value="mask"
                    maxDate={dayjs()}
                    data-test={field.name}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </>
        )}

        <FormGroupLabel alignCenter required={isFieldRequired('productType')} title={TEXT_CONTENT.product} />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          capture
          label="Product"
          name="productType"
          optionList={ProductTypeList.map((value) => ({
            label: value,
            value,
          }))}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('compDescription')}
          title={TEXT_CONTENT.complaintDescription}
        />
        <FormSpacer type="input" />
        <TextFieldController
          multiline
          name="compDescription"
          control={control}
          label="Please type here"
          maxLength={4000}
        />

        <FormSpacer type="formgroup" />
        <Controller
          name="compType"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <RadioGroup
              {...field}
              direction="vertical"
              styleVariant="skinny"
              title={TEXT_CONTENT.complaintType}
              titleSuffix={<InformationPopUp message={TEXT_CONTENT.complaintTypeInfoText} />}
              required={isFieldRequired('compType')}
              defaultValue=""
              radioList={[
                {
                  id: 'sales',
                  label: TEXT_CONTENT.compTypeSalesText,
                  value: 'sales',
                },
                {
                  id: 'claims',
                  label: TEXT_CONTENT.compTypeClaimsText,
                  value: 'claims',
                },
                {
                  id: 'financialHardshipApp',
                  label: TEXT_CONTENT.compTypeFinHardText,
                  value: 'financialHardshipApp',
                },
              ]}
              data-test="compType"
              data-track="compType"
              data-track-value="capture"
            />
          )}
        />

        {isCompTypeEnabled && compCategoryOptions && (
          <>
            <FormSpacer type="formgroup" />
            <Controller
              name="compCategory"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <CheckboxGroupStyled
                  {...field}
                  styleVariant="skinny"
                  required
                  checkboxList={compCategoryOptions.map((option) => ({
                    id: option.value,
                    name: 'compCategory',
                    label: option.text,
                    value: option.value,
                    checked: field.value?.includes(option.value),
                    disabled: !field.value?.includes(option.value) && (field.value?.length ?? 0) >= 2,
                  }))}
                  onChange={(_: any, { checked, value: clickedValue }: any) => {
                    if (checked) {
                      field.onChange([...(field.value ?? []), clickedValue]);
                    } else {
                      field.onChange(field.value?.filter((value) => value !== clickedValue));
                    }
                  }}
                  data-test="compCategory"
                  data-track="compCategory"
                  data-track-value="capture"
                  title={TEXT_CONTENT.complaintCategory}
                  titleSuffix={<InformationPopUp message={TEXT_CONTENT.complaintCategoryInfoText} />}
                />
              )}
            />
          </>
        )}

        <ToggleController
          control={control}
          name="sensitiveCase"
          required
          title={`${TEXT_CONTENT.isSensitive}`}
          titleSuffix={<InformationPopUp message={TEXT_CONTENT.sensitiveInfoText} />}
          options={YesNoList}
          capture
        />

        <ToggleController
          required
          titleSuffix={<InformationPopUp message={TEXT_CONTENT.isResolvedInfoText} />}
          control={control}
          name="resolved"
          options={YesNoList}
          capture
          title={`${TEXT_CONTENT.isResolved}`}
        />

        {resolved === 'no' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('desiredOutcome')}
              title={TEXT_CONTENT.customerDesiredOutcome}
            />
            <FormSpacer type="input" />
            <TextFieldController
              multiline
              name="desiredOutcome"
              control={control}
              label="Please type here"
              maxLength={4000}
            />
          </>
        )}

        {resolved === 'yes' && (
          <>
            <FormGroupLabel title={TEXT_CONTENT.resolvedDate} required alignCenter />
            <FormSpacer type="input" />
            <Controller
              name="resolvedDate"
              control={control}
              render={({ field: { value, ...field }, fieldState: { error } }) => {
                return (
                  <DatePicker
                    {...field}
                    value={dayjs(value as any)}
                    id={field.name}
                    format="DD/MM/YYYY"
                    label="DD/MM/YYYY"
                    data-track={field.name}
                    data-track-value="mask"
                    maxDate={dayjs()}
                    data-test={field.name}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />

            <FormGroupLabel
              alignCenter
              required={isFieldRequired('outcomeType')}
              title={TEXT_CONTENT.complaintOutcome}
            />
            <FormSpacer type="input" />
            <DropdownController
              control={control}
              capture
              label="Outcome"
              name="outcomeType"
              optionList={OutcomeTypeList.map((value) => ({
                label: value,
                value,
              }))}
            />

            <FormGroupLabel
              alignCenter
              required={isFieldRequired('outcomeDesc')}
              title={TEXT_CONTENT.outcomeDescription}
            />
            <FormSpacer type="input" />
            <TextFieldController
              multiline
              name="outcomeDesc"
              control={control}
              label="Please type here"
              maxLength={4000}
            />
          </>
        )}

        <FormGroupLabel alignCenter title={TEXT_CONTENT.amountInDispute} />
        <FormSpacer type="input" />
        <Controller
          name="amountInDispute"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <CurrencyTextField
              {...field}
              value={field.value!}
              id="amountInDispute"
              error={!!error}
              label={TEXT_CONTENT.amountInDisputeLabel}
              data-track="amountInDispute"
              data-track-value="mask"
              helperText={error?.message}
              data-test="amountInDispute"
              maxLength={10}
            />
          )}
        />
        {resolved === 'yes' && (
          <>
            <FormGroupLabel alignCenter title={TEXT_CONTENT.amountResolved} />
            <FormSpacer type="input" />
            <Controller
              name="amountCompResolved"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <CurrencyTextField
                  {...field}
                  value={field.value!}
                  id="amountCompResolved"
                  error={!!errors.amountCompResolved}
                  label={TEXT_CONTENT.amountResolvedLabel}
                  data-track="amountCompResolved"
                  data-track-value="mask"
                  helperText={get(errors, 'amountCompResolved.message', null)}
                  data-test="amountCompResolved"
                  maxLength={10}
                />
              )}
            />
          </>
        )}

        {enableAttachment && (
          <Attachment
            enabled={enableAttachment}
            files={files}
            setFiles={setFiles}
            updateFiles={updateFiles}
            errorMessage={get(errors, 'files.message')}
            maxFileSize={maxFileSize}
          />
        )}
        <FormSpacer type="formgroup" />
        <Button type="submit" label="Submit" disabled={!isValid} data-track="submit" />
      </FormCard>
    </Form>
  );
};

export default ComplaintDetailsForm;
